import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, RotateCcw, Sparkles, CheckCircle2, GraduationCap, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAssessment } from "@/context/AssessmentContext";
import { CTAS_BY_ROLE, RUOLI, COURSE_URL, COACHING_URL, CALL_URL } from "@/data/questions";
import { trackCtaClick } from "@/lib/analytics";
import BDFLogo from "@/components/BDFLogo";

const AREA_LABELS = {
  consapevolezza: "Consapevolezza",
  performance_recupero: "Performance & Recupero",
  gestione_ansia: "Gestione Ansia",
  applicazione_tecnica: "Applicazione Tecnica",
  pratica_didattica: "Pratica Didattica",
  supporto_allievi: "Supporto Allievi",
  sviluppo_professionale: "Sviluppo Professionale",
};

const AREA_MAX = {
  consapevolezza: 20,
  performance_recupero: 20,
  gestione_ansia: 20,
  applicazione_tecnica: 20,
  pratica_didattica: 40,
  supporto_allievi: 20,
  sviluppo_professionale: 30,
};

const TOTALE_MAX = {
  danzatore: 80,
  insegnante: 90,
  entrambi: 170,
};

export default function Results() {
  const navigate = useNavigate();
  const { result, reset } = useAssessment();

  useEffect(() => {
    if (!result) navigate("/", { replace: true });
  }, [result, navigate]);

  if (!result) return null;

  const handleRestart = () => {
    reset();
    navigate("/");
  };

  const ruolo = (result.user_info.ruolo || "").toLowerCase();
  const ctas = CTAS_BY_ROLE[ruolo] || CTAS_BY_ROLE.danzatore;
  const totaleMax = TOTALE_MAX[ruolo] || 100;
  const ruoloLabel = (RUOLI.find((r) => r.value === ruolo) || {}).label || ruolo;

  const ctaTypeFromUrl = (url) => {
    if (!url) return "unknown";
    if (url === CALL_URL || url.includes("calendly.com")) return "call";
    if (url === COACHING_URL || url.includes("coaching")) return "coaching";
    if (url === COURSE_URL || url.includes("corso")) return "course";
    return "unknown";
  };

  const handleCtaClick = (url) => {
    trackCtaClick({
      cta: ctaTypeFromUrl(url),
      url,
      profilo: result.profilo,
      ruolo,
    });
  };

  const areas = Object.entries(AREA_LABELS)
    .filter(([key]) => typeof result.areas[key] === "number")
    .map(([key, label]) => ({
      key,
      label,
      value: result.areas[key],
      max: AREA_MAX[key],
      pct: Math.round((result.areas[key] / AREA_MAX[key]) * 100),
    }));

  return (
    <div className="min-h-screen w-full pb-24">
      <header className="px-6 sm:px-10 lg:px-16 pt-8">
        <div className="flex items-center max-w-[1200px] mx-auto">
          <BDFLogo />
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EFECE7] border border-[#DCE3DF]" data-testid="results-greeting">
            <Sparkles className="w-3.5 h-3.5 text-[#1E3F33]" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#1E3F33] font-medium">
              Ciao {result.user_info.nome}, ecco il tuo profilo
            </span>
          </div>
          <h1
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-[#1E3F33] tracking-tight"
            data-testid="results-profile-name"
          >
            {result.profilo}
          </h1>
          <p className="mt-8 text-base sm:text-lg text-[#5C7368] leading-relaxed" data-testid="results-profile-description">
            {result.profilo_descrizione}
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="results-grid">
          {/* Areas card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2 bg-white rounded-3xl border border-[#DCE3DF] p-8 sm:p-10"
            data-testid="results-areas-card"
          >
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-display text-2xl sm:text-3xl text-[#1E3F33]">
                Le tue aree di valutazione
              </h3>
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#5C7368]">
                Punteggio
              </span>
            </div>
            <div className="space-y-6">
              {areas.map((a, i) => (
                <motion.div
                  key={a.key}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.08 }}
                  data-testid={`area-${a.key}`}
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-sm sm:text-base text-[#1E3F33] font-medium">
                      {a.label}
                    </span>
                    <span className="font-display text-lg text-[#1E3F33]">
                      {a.value}<span className="text-[#5C7368] text-sm">/{a.max}</span>
                    </span>
                  </div>
                  <div className="relative h-[6px] bg-[#EFECE7] rounded-full overflow-hidden">
                    <motion.div
                      className="absolute top-0 left-0 h-full bg-[#1E3F33] rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${a.pct}%` }}
                      transition={{ duration: 0.9, delay: 0.3 + i * 0.08, ease: "easeOut" }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Total */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-[#1E3F33] rounded-3xl p-8 sm:p-10 text-white flex flex-col justify-between min-h-[280px]"
            data-testid="results-total-card"
          >
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">
              Punteggio Totale
            </div>
            <div>
              <div className="font-display text-7xl sm:text-8xl leading-none">
                {result.areas.totale}
              </div>
              <div className="text-white/60 text-sm mt-2">
                su {totaleMax} punti
              </div>
            </div>
            <div className="text-xs text-white/80 leading-relaxed">
              Ruolo: <span className="font-medium">{ruoloLabel}</span><br />
              Stile: <span className="font-medium">{result.user_info.stile_danza}</span>
            </div>
          </motion.div>

          {/* Recommendations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-3 bg-[#EFECE7] rounded-3xl border border-[#DCE3DF] p-8 sm:p-12"
            data-testid="results-recommendations-card"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-full bg-[#1E3F33] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl text-[#1E3F33]">
                Le tue raccomandazioni personalizzate
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {result.raccomandazioni.map((r, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.08 }}
                  className="flex items-start gap-3 bg-white rounded-2xl p-5 border border-[#DCE3DF]"
                  data-testid={`recommendation-${i}`}
                >
                  <CheckCircle2 className="w-5 h-5 text-[#1E3F33] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-sm sm:text-base text-[#2A3B32] leading-relaxed">{r}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Primaria */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="lg:col-span-3 relative overflow-hidden bg-[#1E3F33] rounded-3xl p-8 sm:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-8 text-white"
            data-testid="results-primary-cta-card"
          >
            <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full border border-white/10 pointer-events-none" />
            <div className="absolute -right-16 -bottom-20 w-56 h-56 rounded-full border border-white/10 pointer-events-none" />

            <div className="flex-1 relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <span className="text-[11px] uppercase tracking-[0.2em] text-white/80 font-medium">
                  Il prossimo passo
                </span>
              </div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight" data-testid="primary-cta-title">
                {ctas.primary.title}
              </h3>
              <p className="mt-5 text-base text-white/80 leading-relaxed max-w-xl" data-testid="primary-cta-text">
                {ctas.primary.text}
              </p>
            </div>

            <a
              href={ctas.primary.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCtaClick(ctas.primary.url)}
              data-testid="results-primary-cta-button"
              className="relative z-10 inline-flex items-center justify-center gap-3 rounded-full h-14 px-9 bg-white hover:bg-[#EFECE7] text-[#1E3F33] text-sm tracking-[0.16em] uppercase font-semibold transition-all hover:shadow-2xl group whitespace-nowrap"
            >
              {ctas.primary.button}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>

          {/* CTA Secondaria */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="lg:col-span-3 bg-white rounded-3xl border border-[#DCE3DF] p-8 sm:p-12"
            data-testid="results-secondary-cta-card"
          >
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#EFECE7] flex items-center justify-center">
                    <PhoneCall className="w-4 h-4 text-[#1E3F33]" strokeWidth={1.5} />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#5C7368] font-medium">
                    Percorso personalizzato
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#1E3F33] tracking-tight leading-tight" data-testid="secondary-cta-title">
                  {ctas.secondary.title}
                </h3>
                <p className="mt-3 text-sm sm:text-base text-[#5C7368] leading-relaxed max-w-xl" data-testid="secondary-cta-text">
                  {ctas.secondary.text}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                {ctas.secondary.buttons.map((b, i) => (
                  <a
                    key={b.label}
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleCtaClick(b.url)}
                    data-testid={`results-secondary-cta-button-${i}`}
                    className={`inline-flex items-center justify-center gap-2 rounded-full h-12 px-6 text-xs tracking-[0.16em] uppercase font-semibold transition-all whitespace-nowrap group ${
                      i === 0
                        ? "bg-[#1E3F33] hover:bg-[#2A5243] text-white hover:shadow-xl"
                        : "bg-transparent border border-[#1E3F33]/30 text-[#1E3F33] hover:bg-[#EFECE7]"
                    }`}
                  >
                    {b.label}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </a>
                ))}
              </div>
            </div>
            {ctas.secondary.subtitle && (
              <div className="mt-6 pt-6 border-t border-[#DCE3DF]/60 flex items-center justify-center lg:justify-end">
                <p className="text-xs uppercase tracking-[0.16em] text-[#5C7368]" data-testid="secondary-cta-subtitle">
                  {ctas.secondary.subtitle}
                </p>
              </div>
            )}
          </motion.div>

          {/* Goal recap removed: report now ends with CTA + closing message */}

          {/* Chiusura: "Un ultimo messaggio per te" + firma */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65 }}
            className="lg:col-span-3 bg-white rounded-3xl border border-[#DCE3DF] p-8 sm:p-12"
            data-testid="results-closing-card"
          >
            <h3 className="font-display text-2xl sm:text-3xl text-[#1E3F33] tracking-tight mb-5">
              Un ultimo messaggio per te
            </h3>
            <p className="text-sm sm:text-base text-[#2A3B32] leading-relaxed mb-3">
              Il Metodo BDF non è soltanto una tecnica di respirazione.<br />
              È un modo diverso di vivere la danza, di ascoltare il proprio corpo e di esprimere il proprio potenziale.
            </p>
            <p className="text-sm sm:text-base text-[#2A3B32] leading-relaxed mb-3">
              Questo Assessment non rappresenta un giudizio né un punto di arrivo. È semplicemente il
              punto di partenza di un percorso di crescita personale e professionale.
            </p>
            <p className="text-sm sm:text-base text-[#2A3B32] leading-relaxed mb-3">
              Ogni danzatore e ogni insegnante ha una storia diversa. Per questo motivo credo nel valore
              di un percorso costruito sulla persona.
            </p>
            <p className="text-sm sm:text-base text-[#2A3B32] leading-relaxed mb-6">
              Sarò felice di accompagnarti nel tuo prossimo passo.
            </p>

            <div className="bg-[#EFECE7] border-l-4 border-[#1E3F33] rounded-lg px-6 py-5 mb-6">
              <p className="font-display italic text-base sm:text-lg text-[#1E3F33] leading-relaxed">
                Ogni grande cambiamento nasce da una nuova consapevolezza.<br />
                Oggi hai fatto il primo passo.<br />
                Il resto del percorso possiamo costruirlo insieme.
              </p>
            </div>

            <p className="font-display italic text-lg sm:text-xl text-[#1E3F33]" data-testid="results-signature">
              Eliana Bassotto
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C7368]">
            Metodo BDF · Breath Dance Flow · Eliana Bassotto
          </p>
        </motion.div>
      </main>
    </div>
  );
}
